<template>
  <div class="rich-editor-container">
    <Editor :value="modelValue" :init="editorConfig" />
    <!-- MentionModal -->
    <component
      :is="MentionModalComponent"
      v-model="mentionModalVisible"
      @select="handleUserSelect"
    />
    <!-- LinkModal -->
    <component
      :is="LinkModalComponent"
      v-model="linkModalVisible"
      :link-data="linkModalData"
      @select="handleLinkSelect"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  ref,
  watch,
  defineAsyncComponent,
  onActivated,
} from "vue";
import Editor from "@tinymce/tinymce-vue";
import { useI18n } from "vue-i18n";
import { useVideoStore, type VideoUploadResult } from "@/stores/video";
import { useImageViewerStore } from "@/stores";
import { useToastStore } from "@/stores/toast";
import { useUserStore } from "@/stores/user";
import type { UserInfo } from "@/api";
import { uploadApi } from "@/api";
import Compressor from "compressorjs";
import {
  filterExternalImages,
  extractPlainTextFromHtml,
} from "../../utils/richEditor";
// 最大字符数限制
const MAX_CHARS = 100000;
// 最大文件大小限制
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB

// 检查文件大小
const checkFileSize = (file: File, maxSize: number): boolean => {
  if (file.size > maxSize) {
    toastStore.showToast(
      t("post.create.fileTooLarge", {
        maxSize: `${Math.floor(maxSize / 1024 / 1024)}MB`,
      })
    );
    return false;
  }
  return true;
};

// 声明全局类型
declare global {
  interface Window {
    tinymce: any;
  }
}
// 引入tinymce
import "tinymce/tinymce";
// 引入编辑器主题
import "tinymce/themes/silver/theme";
// 引入中文语言包
import "tinymce/models/dom";
// 引入图标
import "tinymce/icons/default/icons";

// 引入插件
import "tinymce/plugins/lists";
import "tinymce/plugins/image";
import "tinymce/plugins/charmap";
import "tinymce/plugins/media";
import "tinymce/plugins/table";
import "tinymce/plugins/wordcount";

// 引入样式
import "tinymce/skins/ui/oxide/skin.css";
import "tinymce/skins/ui/oxide/content.css";
import "tinymce/skins/content/default/content.min.css";

const { t } = useI18n();
const imageStore = useImageViewerStore();
const toastStore = useToastStore();
const videoStore = useVideoStore();
const userStore = useUserStore();
const props = defineProps<{
  modelValue: string;
  placeholder?: string;
  isPost?: boolean;
}>();
// 文件转base64的工具函数
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

// 添加编辑器实例的引用
const editorInstance = ref<any>(null);

// @功能相关
const mentionModalVisible = ref(false);

// 链接功能相关
const linkModalVisible = ref(false);
const linkModalData = ref<{ text: string; url: string } | null>(null);

// 动态导入MentionModal组件
const MentionModalComponent = defineAsyncComponent(
  () => import("@/components/modal/MentionModal.vue")
);

// 动态导入LinkModal组件
const LinkModalComponent = defineAsyncComponent(
  () => import("@/components/modal/LinkModal.vue")
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

// 添加防抖处理
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const handleChange = (value: string) => {
  // 清除之前的定时器
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  // 设置新的定时器，延迟 50ms 后更新
  debounceTimer = setTimeout(() => {
    emit("update:modelValue", value);
  }, 50);
};

// 公共方法：确保编辑器光标位置正确
const ensureEditorFocusAndPosition = (editor: any) => {
  // 确保编辑器有焦点
  editor.focus();

  // 检查当前光标位置，如果没有选中内容，则将光标移到末尾
  const selection = editor.selection;
  const range = selection.getRng();
  const body = editor.getBody();

  // 如果光标不在编辑器内或者没有有效的位置，将光标移到末尾
  if (
    !range ||
    !body.contains(range.startContainer) ||
    !body.contains(range.endContainer)
  ) {
    // 将光标移到编辑器末尾
    const lastNode = body.lastChild;
    if (lastNode) {
      const newRange = document.createRange();
      newRange.setStartAfter(lastNode);
      newRange.collapse(true);
      selection.setRng(newRange);
    }
  }
};

// 公共方法：将光标设置到指定元素后面
const setCursorAfterElement = (editor: any, element: Element) => {
  const selection = editor.selection;
  const range = document.createRange();
  range.setStartAfter(element);
  range.collapse(true);
  selection.setRng(range);
  editor.focus();
};

// 公共方法：创建文件输入元素并处理文件选择
const createFileInput = (
  accept: string,
  onFileSelect: (file: File) => void
) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = accept;
  input.style.display = "none";

  input.onchange = (e) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  document.body.appendChild(input);
  input.click();
  document.body.removeChild(input);
};

// 公共方法：上传图片并返回图片URL
const uploadImage = async (file: File): Promise<string> => {
  try {
    // 检查文件大小
    if (!checkFileSize(file, MAX_IMAGE_SIZE)) {
      return "";
    }
    // todo压缩图片
    const compressedFile = file;
    const base64 = await fileToBase64(compressedFile);
    const result = await uploadApi.uploadImg(base64);
    if (result.code == 200) {
      return result.data.img_url;
    } else {
      toastStore.showErrorCodeToast(result.code, t);
      return "";
    }
  } catch (error: any) {
    toastStore.showToast(
      t("post.create.imageUploadFailed") + ": " + (error.message || error)
    );
    return "";
  }
};

// 显示@用户选择弹窗
const showMentionModal = () => {
  mentionModalVisible.value = true;
};

// 显示链接选择弹窗
const showLinkModal = () => {
  linkModalData.value = null;
  // 确保清除之前的编辑元素引用
  (window as any).currentEditingLinkElement = null;
  linkModalVisible.value = true;
};

// 显示链接编辑弹窗
const showLinkEditModal = (
  linkData: { text: string; url: string },
  linkElement: HTMLElement
) => {
  linkModalData.value = linkData; // 传入当前链接数据作为初始值
  // 保存链接元素引用，用于后续更新
  (window as any).currentEditingLinkElement = linkElement;
  linkModalVisible.value = true;
};

// 处理用户选择
const handleUserSelect = (user: UserInfo) => {
  if (editorInstance.value) {
    const editor = editorInstance.value;
    const userName = user.nickname || user.username || "unknown";
    const mentionText = `<span class="mention-user" data-user-id="${user.uid}" data-username="${userName}" style="color: #f4b73f; background-color: #f4b73f20; padding: 2px 4px; border-radius: 3px; white-space: nowrap; cursor: pointer;">@${userName}</span> `;

    // 确保编辑器光标位置正确
    ensureEditorFocusAndPosition(editor);

    // 插入@文本到编辑器
    editor.insertContent(mentionText);

    // 确保光标在插入内容之后，不在 mention 元素内
    setTimeout(() => {
      // 找到最后插入的 mention 元素
      const mentionElements = editor
        .getBody()
        .querySelectorAll(".mention-user");
      const lastMentionElement = mentionElements[mentionElements.length - 1];

      if (lastMentionElement) {
        // 将光标设置到 mention 元素后面
        setCursorAfterElement(editor, lastMentionElement);
      }
    }, 10);

    // 触发内容变化
    const content = editor.getContent();
    handleChange(content);
  }
};

// 处理链接选择
const handleLinkSelect = (linkData: { text: string; url: string }) => {
  if (editorInstance.value) {
    // 检查是否是编辑现有链接
    const currentEditingLinkElement = (window as any).currentEditingLinkElement;
    if (currentEditingLinkElement) {
      // 编辑模式：更新现有链接元素
      currentEditingLinkElement.setAttribute("data-title", linkData.text);
      currentEditingLinkElement.setAttribute("data-link", linkData.url);
      currentEditingLinkElement.textContent = linkData.text;

      // 清除引用
      (window as any).currentEditingLinkElement = null;
    } else {
      // 新建模式：插入新链接
      const editor = editorInstance.value;

      // 确保编辑器光标位置正确
      ensureEditorFocusAndPosition(editor);

      const linkHtml = `<span class="post-link" data-title="${linkData.text}" data-link="${linkData.url}" style="color: #4a90e2; background-color: #4a90e220; padding: 2px 4px; border-radius: 3px; white-space: nowrap; cursor: pointer; text-decoration: underline;">${linkData.text}</span> `;
      // 插入链接到编辑器
      editor.insertContent(linkHtml);

      // 确保光标在插入内容之后，不在 link 元素内
      setTimeout(() => {
        // 找到最后插入的 link 元素
        const linkElements = editor.getBody().querySelectorAll(".post-link");
        const lastLinkElement = linkElements[linkElements.length - 1];

        if (lastLinkElement) {
          // 将光标设置到 link 元素后面
          setCursorAfterElement(editor, lastLinkElement);
        }
      }, 10);
    }

    // 触发内容变化
    const content = editorInstance.value.getContent();
    handleChange(content);
  }
};

const editorConfig = computed(() => ({
  height: "100%",
  branding: false,
  menubar: false,
  skin: "oxide",
  skin_url: "/forum/tinymce/skins/ui/oxide",
  content_css: "/forum/tinymce/skins/content/default/content.min.css",
  paste_data_images: true, // 允许粘贴图片
  encoding: "utf8", // 确保UTF-8编码支持
  // 设置静态资源基础路径 - 使用公共目录
  plugins: ["lists", "image", "charmap", "media", "table", "wordcount"],
  toolbar: (() => {
    const baseToolbar =
      "undo redo | custommention customimage " + (props.isPost ? "media " : "");
    if (userStore.isOfficialAccount) {
      return (
        baseToolbar +
        " customlink |" +
        (props.isPost ? " blocks |" : "") +
        "bold italic forecolor | alignleft aligncenter |  "
      );
    } else {
      return baseToolbar;
    }
  })(),
  // "alignright alignjustify | bullist numlist outdent indent | ",
  content_style:
    "body { font-family:Helvetica,Arial,sans-serif; font-size:14px } " +
    ".mce-placeholder { color: #999 !important; font-style: italic; } " +
    ".mention-user { display: inline-block; user-select: all; -webkit-user-modify: read-only; -moz-user-modify: read-only; user-modify: read-only; } " +
    ".post-link { display: inline-block; user-select: all; -webkit-user-modify: read-only; -moz-user-modify: read-only; user-modify: read-only; }",
  placeholder: props.placeholder || t("post.create.contentPlaceholder"),
  elementpath: false, // 隐藏左下角的标签路径
  setup: (editor: any) => {
    // 保存编辑器实例
    editorInstance.value = editor;

    // 添加点击post-link的处理
    editor.on("click", (e: any) => {
      const target = e.target;
      if (
        target &&
        target.classList &&
        target.classList.contains("post-link")
      ) {
        e.preventDefault();
        e.stopPropagation();

        // 获取链接数据
        const linkText =
          target.getAttribute("data-title") || target.textContent || "";
        const linkUrl = target.getAttribute("data-link") || "";

        if (linkText && linkUrl) {
          // 保存当前点击的链接元素，用于后续更新
          const clickedLinkElement = target;

          // 显示编辑模式弹窗
          showLinkEditModal(
            { text: linkText, url: linkUrl },
            clickedLinkElement
          );
        }
      }
    });

    // 允许的图片类型
    const allowedImageTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/bmp",
    ];

    // 检查是否是允许的图片类型
    const isAllowedImageType = (type: string) => {
      return allowedImageTypes.includes(type.toLowerCase());
    };

    // 处理拖拽事件
    editor.on("dragover", (e: any) => {
      e.preventDefault();
    });

    editor.on("drop", async (e: any) => {
      e.preventDefault();

      const items = e.dataTransfer?.items || e.dataTransfer?.files;
      if (!items) return;

      let hasValidImage = false;
      const validFiles: File[] = [];

      // 收集所有有效的图片文件
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const file = item.getAsFile ? item.getAsFile() : item;
        if (file && isAllowedImageType(file.type)) {
          hasValidImage = true;
          validFiles.push(file);
        }
      }
      // 如果有有效图片，开始上传
      if (hasValidImage) {
        toastStore.showToast(t("post.create.imageUploading"));

        for (const file of validFiles) {
          const imageUrl = await uploadImage(file);
          if (imageUrl) {
            const imageHtml = `<img src="${imageUrl}" style="max-width: 100%; height: auto;" alt="picture">`;
            editor.insertContent(imageHtml);
          }
        }

        toastStore.showToast(t("post.create.imageUploadSuccess"));
      }
    });

    // 修改粘贴事件处理，只对文本内容过滤HTML标签，保持图片上传功能
    editor.on("paste", async (e: any) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      // 检查是否包含图片和文本
      let hasImage = false;
      let hasText = false;
      let hasInvalidFile = false;

      for (let i = 0; i < items.length; i++) {
        const type = items[i].type;
        // 检查是否包含非法文件
        if (
          type.indexOf("application/") !== -1 ||
          type.indexOf("video/") !== -1 ||
          (type.indexOf("image/") !== -1 && !isAllowedImageType(type))
        ) {
          hasInvalidFile = true;
          break;
        }
        if (type.indexOf("image/") !== -1 && isAllowedImageType(type)) {
          hasImage = true;
        }
        if (type === "text/html" || type === "text/plain") {
          hasText = true;
        }
      }

      // 如果包含非法文件，阻止粘贴
      if (hasInvalidFile) {
        e.preventDefault();
        toastStore.showToast(
          t("post.create.invalidFileType") || "不支持的文件类型"
        );
        return;
      }

      // 检查字符数限制
      const charCount = editor.plugins.wordcount.body.getCharacterCount();
      const pasteText = e.clipboardData.getData("text").length;
      if (charCount + pasteText > MAX_CHARS) {
        e.preventDefault();
        toastStore.showToast(
          t("post.create.maxCharsReached", { max: MAX_CHARS })
        );
        return false;
      }

      // 如果只有图片，没有文本 - 保持图片上传功能
      if (hasImage && !hasText) {
        e.preventDefault();
        toastStore.showToast(t("post.create.imageUploading"));

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (
            item.type.indexOf("image") !== -1 &&
            isAllowedImageType(item.type)
          ) {
            const file = item.getAsFile();
            if (file) {
              const imageUrl = await uploadImage(file);
              if (imageUrl) {
                const imageHtml = `<img src="${imageUrl}" style="max-width: 100%; height: auto;" alt="picture">`;
                editor.insertContent(imageHtml);
              }
            }
          }
        }

        toastStore.showToast(t("post.create.imageUploadSuccess"));
        return;
      }

      // 如果是图文混合，只保留文本部分，过滤HTML标签
      if (hasImage && hasText) {
        e.preventDefault();
        const plainText = e.clipboardData.getData("text/plain");
        if (plainText) {
          // 将换行符转换为HTML换行标签
          const htmlText = plainText.replace(/\n/g, "<br>");
          editor.insertContent(htmlText);
        }
        return;
      }

      // 处理纯文本粘贴，过滤HTML标签和外部图片
      if (!hasImage && hasText) {
        const html = e.clipboardData.getData("text/html");
        const plainText = e.clipboardData.getData("text/plain");

        if (html) {
          e.preventDefault();
          // 直接从HTML中提取纯文本
          const textContent = extractPlainTextFromHtml(html);

          if (textContent.trim()) {
            // 将换行符转换为HTML换行标签
            const htmlText = textContent.replace(/\n/g, "<br>");
            editor.insertContent(htmlText);
          }
          return;
        } else if (plainText) {
          e.preventDefault();
          // 检查纯文本中是否包含图片URL，如果有则过滤掉
          const filteredText = plainText.replace(
            /https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|bmp)/gi,
            ""
          );

          if (filteredText.trim()) {
            // 将换行符转换为HTML换行标签
            const htmlText = filteredText.replace(/\n/g, "<br>");
            editor.insertContent(htmlText);
          }
          return;
        }
      }
    });

    // 监听输入事件，限制字符数
    editor.on("keydown", (e: any) => {
      const charCount = editor.plugins.wordcount.body.getCharacterCount();
      if (charCount >= MAX_CHARS) {
        e.preventDefault();
        toastStore.showToast(
          t("post.create.maxCharsReached", { max: MAX_CHARS })
        );
        return false;
      }
    });
    // 配置编辑器，过滤外部图片
    editor.on("BeforeSetContent", (e: any) => {
      if (e.content) {
        // 过滤外部图片
        e.content = filterExternalImages(e.content);
      }
    });

    // 配置编辑器，防止 mention 和 link 元素被分割
    editor.on("KeyDown", (e: any) => {
      const selection = editor.selection;
      const node = selection.getNode();

      // 如果当前光标在 mention 或 link 元素内，且按下删除键或退格键
      if (
        (e.keyCode === 8 || e.keyCode === 46) &&
        node.nodeName === "SPAN" &&
        node.className &&
        (node.classList.contains("mention-user") ||
          node.classList.contains("post-link"))
      ) {
        // 阻止默认行为
        e.preventDefault();

        // 删除整个元素
        editor.dom.remove(node);

        // 触发内容变化
        setTimeout(() => {
          const content = editor.getContent();
          handleChange(content);
        }, 0);

        return false;
      }
    });

    // 注册自定义图片上传按钮
    editor.ui.registry.addButton("customimage", {
      tooltip: "image",
      icon: "image",
      onAction: () => {
        createFileInput("image/*", async (file) => {
          // 显示上传进度
          toastStore.showToast(t("post.create.imageUploading"));

          const imageUrl = await uploadImage(file);
          if (imageUrl) {
            // 插入图片到编辑器
            const imageHtml = `<img src="${imageUrl}" style="max-width: 100%; height: auto;" alt="picture">`;
            editor.insertContent(imageHtml);
            editor.windowManager.close();
            // 显示上传成功提示
            toastStore.showToast(t("post.create.imageUploadSuccess"));
          }
        });
      },
    });

    // 注册自定义@按钮
    editor.ui.registry.addButton("custommention", {
      tooltip: "mention",
      icon: "user",
      onAction: () => {
        showMentionModal();
      },
    });

    // 注册自定义链接按钮
    editor.ui.registry.addButton("customlink", {
      tooltip: "link",
      icon: "link",
      onAction: () => {
        showLinkModal();
      },
    });

    // 初始化时设置占位符和重写media命令
    editor.on("init", () => {
      // 手动切换到字符计数模式
      const wordcountElement = editor
        .getContainer()
        .querySelector(".tox-statusbar__wordcount");
      if (wordcountElement) {
        wordcountElement.click();
      }

      // 重写内置media按钮的功能，改为视频上传
      editor.addCommand("mceMedia", () => {
        createFileInput("video/*", async (file) => {
          try {
            // 检查文件大小
            if (!checkFileSize(file, MAX_VIDEO_SIZE)) {
              return;
            }
            // 显示上传进度
            toastStore.showToast(t("post.create.videoUploading"));
            const result = await videoStore.videoUploader.uploadVideo(file);
            // 插入视频到编辑器
            const videoHtml = `<video controls style="max-width: 100%; height: auto;" data-file-id="${result.fileId}" data-verify-content="${result.verifyContent}" data-video-url="${result.videoUrl}">
              <source src="${result.videoUrl}" type="video/mp4">
            </video>`;

            editor.insertContent(videoHtml);
            editor.windowManager.close();

            // 显示上传成功提示
            toastStore.showToast(t("post.create.videoUploadSuccess"));
          } catch (error) {
            toastStore.showToast(t("post.create.videoUploadFailed"));
          }
        });
      });
    });

    // 监听内容变化事件
    editor.on("input keyup change", () => {
      const content = editor.getContent();
      handleChange(content);
    });

    // 监听工具栏操作和内容插入
    editor.on("ExecCommand ObjectResized", () => {
      const content = editor.getContent();
      handleChange(content);
    });
  },
}));

// 监听 modelValue 的变化，同步到编辑器
watch(
  () => props.modelValue,
  (newValue) => {
    if (
      editorInstance.value &&
      newValue !== editorInstance.value.getContent()
    ) {
      editorInstance.value.setContent(newValue);
    }
  }
);
</script>

<style scoped>
:deep(.tox .tox-edit-area::before) {
  border-color: #4a90e2;
  border-width: 1px;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.1);
}

.rich-editor-container {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.tox.tox-tinymce) {
  flex: 1;
  min-height: 200px; /* 设置最小高度，避免太小 */
}
</style>
